import UnifiedMessagesModule from "../components/UnifiedMessagesModule";
import React, { useEffect, useMemo, useState } from "react";
import { EditorSupportPanel as PublisherEditorSupportPanel } from "./PublisherDashboard";

// Yalnızca telefon ve tablet görünümünü sıkılaştırır.
// Masaüstü stilleri, veri akışı ve işlem fonksiyonları değiştirilmez.
function useMobileOnlyResponsiveLayout(scopeClass, enabled = true) {
  useEffect(() => {
    if (!enabled || typeof document === "undefined") return undefined;

    const bodyClass = `${scopeClass}-active`;
    const styleId = `${scopeClass}-style`;
    document.body.classList.add(bodyClass);

    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      const scope = `body.${bodyClass}`;

      style.textContent = `
        @media (max-width: 1023px) {
          ${scope},
          ${scope} #root {
            width: 100%;
            max-width: 100%;
            overflow-x: hidden;
          }

          ${scope} main {
            width: 100%;
            min-width: 0;
            max-width: 100vw;
          }

          ${scope} table {
            display: block;
            width: 100%;
            max-width: 100%;
            overflow-x: auto;
            overscroll-behavior-inline: contain;
            -webkit-overflow-scrolling: touch;
          }

          ${scope} iframe,
          ${scope} video,
          ${scope} canvas {
            max-width: 100%;
          }

          ${scope} [class*="fixed"][class*="inset-0"] {
            overflow-y: auto;
            overscroll-behavior: contain;
          }
        }

        @media (max-width: 767px) {
          ${scope} {
            font-size: 13px;
            -webkit-text-size-adjust: 100%;
            text-size-adjust: 100%;
          }

          ${scope} *,
          ${scope} *::before,
          ${scope} *::after {
            box-sizing: border-box;
          }

          ${scope} h1,
          ${scope} [class~="text-5xl"],
          ${scope} [class~="text-4xl"] {
            font-size: clamp(1.7rem, 7.5vw, 2rem) !important;
            line-height: 1.12 !important;
            letter-spacing: -0.025em !important;
          }

          ${scope} h2,
          ${scope} [class~="text-3xl"] {
            font-size: clamp(1.35rem, 5.8vw, 1.6rem) !important;
            line-height: 1.18 !important;
          }

          ${scope} h3,
          ${scope} [class~="text-2xl"] {
            font-size: 1.15rem !important;
            line-height: 1.25 !important;
          }

          ${scope} [class~="text-xl"] {
            font-size: 1rem !important;
            line-height: 1.35 !important;
          }

          ${scope} p,
          ${scope} li {
            line-height: 1.55;
          }

          ${scope} [class~="p-10"],
          ${scope} [class~="p-9"],
          ${scope} [class~="p-8"],
          ${scope} [class~="p-7"],
          ${scope} [class~="p-6"],
          ${scope} [class~="p-5"] {
            padding: 1rem !important;
          }

          ${scope} [class~="px-10"],
          ${scope} [class~="px-9"],
          ${scope} [class~="px-8"],
          ${scope} [class~="px-7"],
          ${scope} [class~="px-6"],
          ${scope} [class~="px-5"] {
            padding-left: 1rem !important;
            padding-right: 1rem !important;
          }

          ${scope} [class~="py-10"],
          ${scope} [class~="py-9"],
          ${scope} [class~="py-8"],
          ${scope} [class~="py-7"],
          ${scope} [class~="py-6"] {
            padding-top: 1rem !important;
            padding-bottom: 1rem !important;
          }

          ${scope} [class~="gap-10"],
          ${scope} [class~="gap-9"],
          ${scope} [class~="gap-8"],
          ${scope} [class~="gap-7"],
          ${scope} [class~="gap-6"],
          ${scope} [class~="gap-5"] {
            gap: 0.8rem !important;
          }

          ${scope} [class~="mt-10"],
          ${scope} [class~="mt-9"],
          ${scope} [class~="mt-8"],
          ${scope} [class~="mt-7"],
          ${scope} [class~="mt-6"] {
            margin-top: 1rem !important;
          }

          ${scope} [class~="mb-10"],
          ${scope} [class~="mb-9"],
          ${scope} [class~="mb-8"],
          ${scope} [class~="mb-7"],
          ${scope} [class~="mb-6"] {
            margin-bottom: 1rem !important;
          }

          ${scope} [class*="grid-cols-2"],
          ${scope} [class*="grid-cols-3"],
          ${scope} [class*="grid-cols-4"],
          ${scope} [class*="grid-cols-5"],
          ${scope} [class*="grid-cols-6"],
          ${scope} [class*="grid-cols-["] {
            grid-template-columns: minmax(0, 1fr) !important;
          }

          ${scope} form [class~="flex"] {
            flex-wrap: wrap;
          }

          ${scope} button,
          ${scope} [role="button"] {
            max-width: 100%;
            min-height: 38px;
            white-space: normal;
            touch-action: manipulation;
          }

          ${scope} input,
          ${scope} select,
          ${scope} textarea {
            width: 100%;
            max-width: 100%;
            min-height: 42px;
            font-size: 16px !important;
          }

          ${scope} textarea {
            min-height: 96px;
          }

          ${scope} table {
            font-size: 11px;
          }

          ${scope} th,
          ${scope} td {
            padding: 0.55rem 0.65rem !important;
            white-space: nowrap;
          }

          ${scope} svg[class*="h-["],
          ${scope} canvas[class*="h-["] {
            height: 230px !important;
            max-height: 42vh;
          }

          ${scope} iframe {
            min-height: 230px !important;
            height: 48vh !important;
          }

          ${scope} video {
            max-height: 52vh;
          }

          ${scope} img {
            max-width: 100%;
          }

          ${scope} [class*="fixed"][class*="inset-0"] {
            align-items: flex-start !important;
            padding: max(0.75rem, env(safe-area-inset-top)) 0.75rem max(0.75rem, env(safe-area-inset-bottom)) !important;
          }

          ${scope} [class*="fixed"][class*="inset-0"] > [class*="max-w-"] {
            width: 100% !important;
            max-width: 100% !important;
            max-height: calc(100dvh - 1.5rem) !important;
            margin: auto !important;
            overflow-y: auto !important;
          }

          ${scope} [class*="rounded-[2"],
          ${scope} [class*="rounded-[3"] {
            border-radius: 1.1rem !important;
          }

          ${scope} [class*="tracking-[0.3"],
          ${scope} [class*="tracking-[0.4"] {
            letter-spacing: 0.16em !important;
          }

          ${scope} [class*="max-w-7xl"],
          ${scope} [class*="max-w-6xl"],
          ${scope} [class*="max-w-5xl"],
          ${scope} [class*="max-w-4xl"],
          ${scope} [class*="max-w-3xl"] {
            max-width: 100% !important;
          }
        }

        @media (max-width: 380px) {
          ${scope} h1,
          ${scope} [class~="text-5xl"],
          ${scope} [class~="text-4xl"] {
            font-size: 1.55rem !important;
          }

          ${scope} h2,
          ${scope} [class~="text-3xl"] {
            font-size: 1.25rem !important;
          }

          ${scope} button,
          ${scope} [role="button"] {
            font-size: 11px !important;
          }
        }
      `;

      document.head.appendChild(style);
    }

    return () => {
      document.body.classList.remove(bodyClass);
    };
  }, [scopeClass, enabled]);
}
const API_BASE = "/api";

const STATUS_OPTIONS = [
  { value: "received", label: "Talep Alındı" },
  { value: "reviewing", label: "İncelemede" },
  { value: "waiting_price_approval", label: "Ücret Onayı Bekliyor" },
  { value: "in_progress", label: "İşleme Alındı" },
  { value: "waiting_revision", label: "Revize Bekliyor" },
  { value: "delivered", label: "Teslim Edildi" },
  { value: "cancelled", label: "İptal Edildi" },
];

const TECHNICAL_NEED_OPTIONS = [
  { id: 1, serviceName: "Kısa Video Editi", category: "Video Edit" },
  { id: 2, serviceName: "Yayın Kesiti Montajı", category: "Yayın Kesiti" },
  { id: 3, serviceName: "Canlı Yayın Highlight Videosu", category: "Highlight" },
  { id: 4, serviceName: "Yayından En İyi Anları Seçme", category: "Seçki" },
  { id: 5, serviceName: "Profil Tanıtım Videosu", category: "Profil" },
  { id: 6, serviceName: "Altyazılı Video Hazırlama", category: "Altyazı" },
  { id: 7, serviceName: "Trend Akıma Uyarlama", category: "Trend" },
  { id: 8, serviceName: "Kapak Görseli + Video Paketi", category: "Paket" },
  { id: 9, serviceName: "Logo Düzenleme", category: "Görsel" },
  { id: 10, serviceName: "Başarı Ödül / Etkinlik Duyuru Videosu", category: "Duyuru" },
  { id: 11, serviceName: "Yayın Açılış Videosu", category: "Açılış" },
  { id: 12, serviceName: "Yayın Kapanış / Teşekkür Videosu", category: "Kapanış" },
  { id: 13, serviceName: "Haftalık Performans Videosu", category: "Performans" },
  { id: 14, serviceName: "Yeni Takipçi Çekme Videosu", category: "Büyüme" },
  { id: 15, serviceName: "Özel Tasarım Talebi", category: "Özel" },
];

const CONTENT_GOAL_OPTIONS = [
  "Yeni takipçi çekmek",
  "Yayına izleyici çekmek",
  "Profilimi daha profesyonel göstermek",
  "Viral kısa video çıkarmak",
  "Başarı / kademe duyurusu yapmak",
  "Etkinlik / ödül duyurusu paylaşmak",
  "Mevcut videoyu daha kaliteli hale getirmek",
];

const FORMAT_OPTIONS = [
  "TikTok formatına uyarlansın",
  "Instagram formatına uyarlansın",
  "YouTube formatına uyarlansın",
  "9:16 dikey formata getirilsin",
  "1:1 kare formata getirilsin",
  "16:9 yatay formata getirilsin",
  "Görsel TikTok ekranına uygun hazırlansın",
  "Görsel Instagram ekranına uygun hazırlansın",
  "Görsel YouTube ekranına uygun hazırlansın",
];

const MOOD_OPTIONS = [
  "Dinamik / hızlı",
  "Sinema / premium görünüm",
  "Minimal / temiz",
  "Komik / eğlenceli",
  "Duygusal / soft",
  "Trend / sosyal medya dili",
];

const TECHNICAL_GROUPS = [
  { title: "Yazı / Altyazı", items: ["Altyazı eklensin", "Başlık yazısı eklensin", "Önemli kelimeler vurgulansın", "Konuşmaya göre dinamik altyazı yapılsın", "Emoji / sticker kullanılsın"] },
  { title: "Kurgu / Kesim", items: ["Müzik ritmine kesilsin", "Hızlı geçiş efektleri kullanılsın", "Uzun videodan en iyi an seçilsin", "Gereksiz boşluklar kesilsin", "Video daha akıcı hale getirilsin", "1 revize hakkı dahil olsun"] },
  { title: "Efekt / Görsel Stil", items: ["Zoom / shake efekti olsun", "TikTok trend formatına uyarlansın", "Sinema / premium görünüm verilsin", "Renk düzenlemesi yapılsın", "Geçiş efektleri eklensin"] },
  { title: "Ses / Kalite", items: ["Arka plan ses temizlensin", "Ses seviyesi dengelensin", "Yüz ve ışık iyileştirilsin", "Video kalite iyileştirmesi yapılsın", "Görüntü netleştirilsin"] },
  { title: "Marka / Kimlik", items: ["Logo eklensin", "Kullanıcı adı eklensin", "Profil bilgisi eklensin", "Kapak görseli hazırlansın", "Yayıncı ismi öne çıkarılsın"] },
  { title: "Format / Platform", items: FORMAT_OPTIONS },
  { title: "İçerik Amacı", items: ["Yeni takipçi çekmeye yönelik olsun", "Yayına izleyici çekmeye yönelik olsun", "Başarı / kademe vurgulansın", "Etkinlik / ödül duyurusu gibi hazırlansın", "Profil tanıtımına uygun olsun"] },
];

const STUDIO_TOOLS = [
  { name: "Adobe Premiere Pro", text: "Kesim, dikey format, caption ve sosyal medya çıkışları" },
  { name: "Adobe After Effects", text: "Motion başlık, intro, geçiş ve efekt çalışmaları" },
  { name: "Adobe Photoshop", text: "Kapak, profil görseli ve logo düzenleme" },
  { name: "CapCut / Descript", text: "AI altyazı, hızlı sosyal kurgu ve ses temizleme" },
  { name: "Runway AI", text: "AI destekli kreatif video geliştirme" },
  { name: "Frame.io mantığı", text: "Yorum, teslim ve revize akışı" },
];

const AI_CAPABILITIES = [
  "AI altyazı ve caption hazırlığı",
  "Konuşmadan metin çıkarma",
  "Kısa format için en iyi an seçimi",
  "TikTok / Reels / Shorts oran uyarlama",
  "Ses ve boşluk temizleme",
  "Hook ve ilk 3 saniye güçlendirme",
];

// Not: Bu videolar sadece "içerik boş görünmesin" diye eklenen genel amaçlı
// (telifsiz / CC0) örnek videolardır. Edit Admin panelinden gerçek showreel
// videoların yüklendiği anda bu liste otomatik olarak DB verisiyle değişir
// (bkz. refreshAll -> /editor-sample-videos.php).
const DEMO_VIDEO_A = "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4";
const DEMO_VIDEO_B = "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/friday.mp4";

const FALLBACK_SAMPLES = [
  {
    id: 1,
    title: "Yayından Viral Kesit",
    category: "Yayın Kesiti",
    description: "Canlı yayındaki güçlü anı hızlı kesim, altyazı, vurgu ve dikey formatla paylaşılabilir videoya dönüştürür.",
    videoUrl: DEMO_VIDEO_A,
    status: "active",
    tags: ["Kesit", "Altyazı", "9:16"],
    formatRatio: "9:16",
  },
  {
    id: 2,
    title: "Caption + Hook Edit",
    category: "Altyazı",
    description: "İlk saniyede dikkat çeken başlık, dinamik altyazı ve sosyal medya ritmine uygun kısa kurgu.",
    videoUrl: DEMO_VIDEO_B,
    status: "active",
    tags: ["Hook", "Caption", "Trend"],
    formatRatio: "9:16",
  },
  {
    id: 3,
    title: "Profil Tanıtım Akışı",
    category: "Profil",
    description: "Yayıncının tarzını, yayın saatini ve profil gücünü öne çıkaran kısa tanıtım videosu.",
    videoUrl: DEMO_VIDEO_A,
    status: "active",
    tags: ["Profil", "Tanıtım", "Story"],
    formatRatio: "9:16",
  },
  {
    id: 4,
    title: "Trend Akıma Uyarlama",
    category: "Trend",
    description: "Mevcut görüntüyü TikTok/Reels/Shorts diline göre ritim, geçiş, caption ve format açısından yeniden düzenler.",
    videoUrl: DEMO_VIDEO_B,
    status: "active",
    tags: ["Trend", "Reels", "Shorts"],
    formatRatio: "9:16",
  },
  {
    id: 5,
    title: "Haftanın Highlight Videosu",
    category: "Highlight",
    description: "Haftanın en güçlü birkaç anını tek videoda birleştirip ritmik geçişlerle akıcı bir özet hazırlar.",
    videoUrl: DEMO_VIDEO_A,
    status: "active",
    tags: ["Highlight", "Özet", "Ritim"],
    formatRatio: "9:16",
  },
  {
    id: 6,
    title: "Yayın Açılış Videosu",
    category: "Açılış",
    description: "Yayına markalı ve dikkat çekici bir giriş yapmak için kısa, enerjik açılış videosu üretir.",
    videoUrl: DEMO_VIDEO_B,
    status: "active",
    tags: ["Açılış", "İntro", "Marka"],
    formatRatio: "9:16",
  },
  {
    id: 7,
    title: "Teşekkür / Kapanış Videosu",
    category: "Kapanış",
    description: "Yayın sonunda izleyiciye teşekkür eden, takibe yönlendiren sıcak bir kapanış kurgusu.",
    videoUrl: DEMO_VIDEO_A,
    status: "active",
    tags: ["Kapanış", "Teşekkür", "Yönlendirme"],
    formatRatio: "9:16",
  },
  {
    id: 8,
    title: "Kapak + Kısa Video Paketi",
    category: "Paket",
    description: "Kapak görseli, başlık yazısı ve kısa video edit birlikte tek pakette hazırlanır.",
    videoUrl: DEMO_VIDEO_B,
    status: "active",
    tags: ["Paket", "Kapak", "Tasarım"],
    formatRatio: "9:16",
  },
];

const FALLBACK_REQUESTS = [
  {
    id: 101,
    requestNo: "EDT-2026-0001",
    publisherName: "Ayşe Yıldız",
    username: "ayseyildiz",
    hiddenId: "WM-45821",
    representative: "Mert Kaya",
    serviceId: 2,
    serviceName: "Yayın Kesiti Montajı",
    sampleVideoId: 1,
    description: "Teknik ihtiyaç: Yayın Kesiti Montajı\nİçerik hedefi: Viral kısa video çıkarmak\nEk teknik seçenekler: Altyazı eklensin, Hızlı geçiş efektleri kullanılsın, 1 revize hakkı dahil olsun\n\nYayıncı açıklaması:\nYayındaki en güçlü anı kesip TikTok için hazırlayalım.",
    urgency: "fast",
    estimatedPrice: 0,
    finalPrice: "",
    status: "reviewing",
    adminNote: "Dosya inceleniyor. Ücret ve teslim süresi girilecek.",
    uploadedFileName: "yayin-kesiti.mp4",
    deliveryFileName: "",
    createdAt: "24.05.2026 13:20",
    updatedAt: "24.05.2026 14:10",
  },
];

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function safeNumber(value) {
  return Number(value) || 0;
}

function formatMoney(value) {
  if (!value && value !== 0) return "-";
  return new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(Number(value));
}

function statusLabel(status) {
  return STATUS_OPTIONS.find((item) => item.value === status)?.label || status || "Talep Alındı";
}

function getServiceName(id, fallback) {
  return fallback || TECHNICAL_NEED_OPTIONS.find((item) => String(item.id) === String(id))?.serviceName || id || "-";
}

function getSample(sampleVideos, id) {
  return sampleVideos.find((item) => String(item.id) === String(id));
}

function normalizeTags(tags) {
  if (Array.isArray(tags)) return tags;
  return String(tags || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function getSamplePreviewUrl(video = {}) {
  return video.previewVideoUrl || video.videoUrl || video.videoPath || "";
}

function getSampleDetailUrl(video = {}) {
  return video.detailVideoUrl || video.longVideoUrl || video.videoUrl || video.videoPath || "";
}

function getSampleDurationText(video = {}) {
  return video.formatRatio || video.platform || "9:16";
}

function firstRequestValue(source = {}, keys = [], fallback = "") {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return fallback;
}

function parseArrayValue(value) {
  if (Array.isArray(value)) return value;
  if (!value) return [];

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }

  return [];
}

function normalizeRequestAttachments(request = {}) {
  const rawFiles = firstRequestValue(
    request,
    ["files", "uploadedFiles", "uploaded_files", "attachments", "requestFiles", "request_files"],
    []
  );

  const parsedFiles = parseArrayValue(rawFiles)
    .map((item, index) => {
      if (typeof item === "string") {
        return {
          id: `file-${index}`,
          name: item.split("/").pop() || `Dosya ${index + 1}`,
          url: item.startsWith("/") || item.startsWith("http") ? item : "",
          category: "",
        };
      }

      return {
        id: item.id || item.fileId || item.file_id || `file-${index}`,
        name:
          item.fileName ||
          item.file_name ||
          item.originalName ||
          item.original_name ||
          item.name ||
          `Dosya ${index + 1}`,
        url: item.fileUrl || item.file_url || item.url || item.path || "",
        category: item.category || item.fileCategory || item.file_category || "",
      };
    })
    .filter((item) => item.name || item.url);

  if (parsedFiles.length > 0) return parsedFiles;

  const names = parseArrayValue(
    firstRequestValue(request, ["fileNames", "file_names", "uploadedFileNames", "uploaded_file_names"], [])
  );
  const urls = parseArrayValue(
    firstRequestValue(request, ["fileUrls", "file_urls", "uploadedFileUrls", "uploaded_file_urls"], [])
  );
  const categories = parseArrayValue(
    firstRequestValue(request, ["fileCategories", "file_categories"], [])
  );

  const parallelFiles = Array.from({ length: Math.max(names.length, urls.length, categories.length) })
    .map((_, index) => ({
      id: `parallel-file-${index}`,
      name: names[index] || urls[index]?.split("/").pop() || `Dosya ${index + 1}`,
      url: urls[index] || "",
      category: categories[index] || "",
    }))
    .filter((item) => item.name || item.url);

  if (parallelFiles.length > 0) return parallelFiles;

  const singleName = firstRequestValue(
    request,
    ["uploadedFileName", "uploaded_file_name", "fileName", "file_name"],
    ""
  );
  const singleUrl = firstRequestValue(
    request,
    ["uploadedFileUrl", "uploaded_file_url", "fileUrl", "file_url"],
    ""
  );

  return singleName || singleUrl
    ? [
        {
          id: "single-file",
          name: singleName || String(singleUrl).split("/").pop() || "Yüklenen dosya",
          url: singleUrl,
          category: firstRequestValue(request, ["fileCategory", "file_category"], ""),
        },
      ]
    : [];
}

function urgencyLabel(value) {
  if (value === "urgent") return "Acil";
  if (value === "fast") return "Öncelikli";
  return "Standart";
}

function urgencyClass(value) {
  if (value === "urgent") return "border-red-400/30 bg-red-500/10 text-red-200";
  if (value === "fast") return "border-orange-400/30 bg-orange-500/10 text-orange-200";
  return "border-white/10 bg-white/[0.04] text-white/65";
}

function requestDetailRows(request = {}) {
  return [
    { label: "Talep Başlığı", value: firstRequestValue(request, ["requestTitle", "request_title", "title"]) },
    { label: "Platform", value: firstRequestValue(request, ["platform", "targetPlatform", "target_platform"]) },
    { label: "İçerik Kategorisi", value: firstRequestValue(request, ["contentCategory", "content_category", "category"]) },
    { label: "Video Formatı", value: firstRequestValue(request, ["videoFormat", "video_format", "formatType", "format_type"]) },
    { label: "Video Süresi", value: firstRequestValue(request, ["videoDuration", "video_duration", "duration"]) },
    { label: "Edit Tarzı", value: firstRequestValue(request, ["editStyle", "edit_style", "mood"]) },
    { label: "Müzik Tercihi", value: firstRequestValue(request, ["musicPreference", "music_preference"]) },
    { label: "Altyazı Tercihi", value: firstRequestValue(request, ["subtitlePreference", "subtitle_preference"]) },
    { label: "Teslim Beklentisi", value: firstRequestValue(request, ["deliveryDate", "delivery_date"]) },
    { label: "Referans Bağlantı", value: firstRequestValue(request, ["referenceUrl", "reference_url"]) },
  ].filter((item) => item.value !== undefined && item.value !== null && String(item.value).trim() !== "");
}

function requestStatusClass(status) {
  if (status === "delivered") return "border-emerald-400/30 bg-emerald-500/10 text-emerald-200";
  if (status === "in_progress") return "border-fuchsia-400/30 bg-fuchsia-500/10 text-fuchsia-200";
  if (status === "waiting_revision") return "border-pink-400/30 bg-pink-500/10 text-pink-200";
  if (status === "cancelled") return "border-red-400/30 bg-red-500/10 text-red-200";
  if (status === "waiting_price_approval") return "border-orange-400/30 bg-orange-500/10 text-orange-200";
  return "border-yellow-400/30 bg-yellow-500/10 text-yellow-200";
}

async function apiJson(url, options = {}) {
  const response = await fetch(`${API_BASE}${url}`, { credentials: "include", ...options });
  const text = await response.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    const preview = String(text || "").replace(/\s+/g, " ").slice(0, 160);
    throw new Error(`API JSON döndürmedi: ${preview}`);
  }
  if (!response.ok || data.success === false) {
    throw new Error(data.message || "API işlemi başarısız oldu.");
  }
  return data;
}

function PanelCard({ children, className = "" }) {
  return <div className={cn("rounded-[1.7rem] border border-[#c9a24a]/20 bg-[#101010]/90 p-5 shadow-2xl shadow-black/30", className)}>{children}</div>;
}

function StatusBadge({ status }) {
  return <span className={cn("inline-flex rounded-full border px-3 py-1 text-xs font-bold", requestStatusClass(status))}>{statusLabel(status)}</span>;
}

function StudioHero({ sampleVideos, heroSampleId, setHeroSampleId, onRequest, requests }) {
  const activeSamples = useMemo(() => sampleVideos.filter((item) => (item.status || "active") === "active"), [sampleVideos]);
  const heroSample = activeSamples.find((item) => String(item.id) === String(heroSampleId)) || activeSamples[0] || FALLBACK_SAMPLES[0];
  const summary = {
    total: requests.length,
    active: requests.filter((item) => ["received", "reviewing", "in_progress", "waiting_price_approval", "waiting_revision"].includes(item.status)).length,
    delivered: requests.filter((item) => item.status === "delivered").length,
  };

  useEffect(() => {
    if (activeSamples.length <= 1) return undefined;

    const timer = window.setInterval(() => {
      setHeroSampleId((currentId) => {
        const currentIndex = activeSamples.findIndex((item) => String(item.id) === String(currentId));
        const nextSample = activeSamples[(currentIndex + 1 + activeSamples.length) % activeSamples.length] || activeSamples[0];
        return String(nextSample?.id || activeSamples[0]?.id || "1");
      });
    }, 5200);

    return () => window.clearInterval(timer);
  }, [activeSamples, setHeroSampleId]);

  return (
    <section className="relative overflow-hidden rounded-[2.4rem] border border-yellow-500/25 bg-[radial-gradient(circle_at_top_right,rgba(234,179,8,.30),transparent_32%),linear-gradient(135deg,rgba(234,179,8,.14),rgba(255,255,255,.04)_36%,rgba(0,0,0,.84))] p-5 shadow-[0_30px_100px_rgba(0,0,0,.45)]">
      <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-yellow-400/10 blur-3xl" />
      <div className="relative z-10 grid gap-6 xl:grid-cols-[1fr_.95fr]">
        <div className="flex flex-col justify-between rounded-[1.9rem] border border-yellow-500/20 bg-black/55 p-7">
          <div>
            <div className="text-[11px] font-black uppercase tracking-[0.42em] text-yellow-300">Wolf Edit Studio</div>
            <h2 className="mt-5 max-w-2xl text-4xl font-black leading-tight text-white lg:text-5xl">Yayın anını <span className="text-yellow-300">dikkat çeken içeriklere</span> dönüştür.</h2>
            <p className="mt-5 max-w-xl text-sm leading-7 text-white/62">Yayın kesiti, profil tanıtımı, altyazılı kısa video, kapak görseli ve trend uyarlama taleplerini tek yerden ilet. Aşağıdaki ilham videolarından birini seç, benzerini iste.</p>
          </div>
          <div className="mt-7 grid gap-3 sm:grid-cols-3">
            <Metric title="Toplam Talep" value={summary.total} tone="yellow" />
            <Metric title="İşlemde" value={summary.active} tone="purple" />
            <Metric title="Teslim" value={summary.delivered} tone="green" />
          </div>
        </div>

        <div className="w-full max-w-[820px] justify-self-end rounded-[1.9rem] border border-yellow-500/20 bg-black/45 p-4">
          <div className="group relative overflow-hidden rounded-[1.45rem] border border-yellow-500/25 bg-black shadow-[0_0_55px_rgba(234,179,8,.08)]">
            <div className="relative aspect-video w-full overflow-hidden">
              <video key={heroSample?.id || heroSample?.title} src={heroSample?.videoUrl || heroSample?.videoPath} muted loop playsInline autoPlay controls className="h-full w-full object-cover opacity-90 transition duration-500 group-hover:scale-[1.02] group-hover:opacity-100" />
              <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,.18)_0%,rgba(0,0,0,.08)_28%,rgba(0,0,0,.72)_100%)]" />
              <div className="absolute left-4 right-4 top-4 flex flex-wrap items-center justify-between gap-2">
                <div className="inline-flex rounded-full border border-yellow-400/35 bg-black/60 px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-yellow-200">16:9 Showcase Preview</div>
                <div className="inline-flex rounded-full border border-white/10 bg-black/45 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white/75">TikTok • Reels • Shorts</div>
              </div>
              <div className="absolute bottom-4 left-4 right-4">
                <div className="inline-flex rounded-full border border-yellow-400/30 bg-yellow-500/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-yellow-200">{heroSample?.category || "Edit Showcase"}</div>
                <div className="mt-3 text-xl font-black text-white lg:text-2xl">{heroSample?.title}</div>
                <p className="mt-2 max-w-xl text-xs leading-5 text-white/70">{heroSample?.description}</p>
              </div>
            </div>
          </div>
          {activeSamples.length > 1 ? (
            <div className="mt-3 flex items-center justify-center gap-1.5">
              {activeSamples.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setHeroSampleId(String(item.id))}
                  className={cn(
                    "h-1.5 rounded-full transition-all",
                    String(item.id) === String(heroSample?.id) ? "w-6 bg-yellow-400" : "w-1.5 bg-white/20 hover:bg-white/40"
                  )}
                  aria-label={item.title}
                />
              ))}
            </div>
          ) : null}
          <ScrollingTechnicalNeeds items={TECHNICAL_NEED_OPTIONS.map((item) => item.serviceName)} />
        </div>
      </div>
    </section>
  );
}

function Metric({ title, value, tone }) {
  const cls = tone === "green" ? "border-emerald-400/20 bg-emerald-500/10 text-emerald-200/80" : tone === "purple" ? "border-fuchsia-400/20 bg-fuchsia-500/10 text-fuchsia-200/80" : "border-yellow-500/20 bg-yellow-500/10 text-yellow-300/80";
  return <div className={cn("rounded-2xl border p-4", cls)}><div className="text-2xl font-black text-white">{value}</div><div className="mt-1 text-[10px] font-bold uppercase tracking-[0.18em]">{title}</div></div>;
}

function ScrollingTechnicalNeeds({ items = [] }) {
  const labels = [...items, ...items].filter(Boolean);

  return (
    <div className="mt-3 overflow-hidden rounded-2xl border border-yellow-500/20 bg-black/55 py-2.5">
      <style>{`@keyframes wolfTechTicker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }`}</style>
      <div className="flex w-max items-center gap-3 px-3" style={{ animation: "wolfTechTicker 28s linear infinite" }}>
        {labels.map((label, index) => (
          <span key={`${label}-${index}`} className="inline-flex shrink-0 items-center rounded-full border border-yellow-400/25 bg-yellow-500/10 px-3.5 py-1.5 text-[11px] font-bold text-yellow-100">
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

function ToolAndAiStrip() {
  return (
    <div className="grid gap-4 xl:grid-cols-[1fr_.9fr]">
      <PanelCard>
        <div className="text-xs font-black uppercase tracking-[0.24em] text-yellow-300">Profesyonel kurgu altyapımız</div>
        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {STUDIO_TOOLS.map((tool) => <div key={tool.name} className="rounded-2xl border border-white/10 bg-white/[0.035] p-4 hover:border-yellow-500/25 hover:bg-yellow-500/[0.055]"><div className="text-sm font-black text-white">{tool.name}</div><p className="mt-2 text-xs leading-5 text-white/45">{tool.text}</p></div>)}
        </div>
      </PanelCard>
      <PanelCard className="bg-yellow-500/[0.055]">
        <div className="text-xs font-black uppercase tracking-[0.24em] text-yellow-300">AI destekli üretim</div>
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          {AI_CAPABILITIES.map((item) => <div key={item} className="rounded-2xl border border-white/10 bg-black/30 px-3 py-2 text-xs font-bold text-white/62">✦ {item}</div>)}
        </div>
      </PanelCard>
    </div>
  );
}

function PublisherEditorSupport({ requests, setRequests, sampleVideos, setSampleVideos, refreshAll }) {
  const [activeTab, setActiveTab] = useState("new");
  const [heroSampleId, setHeroSampleId] = useState("1");
  const [serviceId, setServiceId] = useState("1");
  const [sampleId, setSampleId] = useState("1");
  const [priority, setPriority] = useState("normal");
  const [contentGoal, setContentGoal] = useState(CONTENT_GOAL_OPTIONS[0]);
  const [formatType, setFormatType] = useState(FORMAT_OPTIONS[0]);
  const [mood, setMood] = useState(MOOD_OPTIONS[0]);
  const [technicalNeeds, setTechnicalNeeds] = useState(["Altyazı eklensin", "TikTok trend formatına uyarlansın", "1 revize hakkı dahil olsun"]);
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const selectedService = TECHNICAL_NEED_OPTIONS.find((item) => String(item.id) === String(serviceId)) || TECHNICAL_NEED_OPTIONS[0];
  const selectedSample = getSample(sampleVideos, sampleId);

  function openRequestForm(sample) {
    setSampleId(String(sample?.id || ""));
    setHeroSampleId(String(sample?.id || heroSampleId));
    setActiveTab("new");
  }

  function toggleNeed(value) {
    if (value === "1 revize hakkı dahil olsun") return;
    setTechnicalNeeds((prev) => prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]);
  }

  function buildDescription() {
    return [
      `Teknik ihtiyaç: ${selectedService.serviceName}`,
      `İçerik hedefi: ${contentGoal}`,
      `Format / süre: ${formatType}`,
      `Görsel stil: ${mood}`,
      `Ek teknik seçenekler: ${technicalNeeds.join(", ")}`,
      selectedSample ? `Referans örnek: ${selectedSample.title}` : "Referans örnek: Seçilmedi",
      "Revize hakkı: Her talep için 1 revize hakkı dahildir.",
      "",
      "Yayıncı açıklaması:",
      description,
    ].join("\n");
  }

  async function submit(event) {
    event.preventDefault();
    if (!description.trim() && !file) {
      alert("Talep için açıklama yazın veya dosya yükleyin.");
      return;
    }
    try {
      setSaving(true);
      const formData = new FormData();
      formData.append("serviceId", serviceId);
      formData.append("serviceName", selectedService.serviceName);
      formData.append("sampleVideoId", sampleId);
      formData.append("urgency", priority);
      formData.append("contentGoal", contentGoal);
      formData.append("formatType", formatType);
      formData.append("mood", mood);
      formData.append("technicalNeeds", technicalNeeds.join(", "));
      formData.append("revisionRight", "1");
      formData.append("description", buildDescription());
      formData.append("estimatedPrice", "0");
      if (file) formData.append("file", file);
      await apiJson("/editor-request-create.php", { method: "POST", body: formData });
      setDescription("");
      setFile(null);
      setActiveTab("requests");
      await refreshAll();
    } catch (error) {
      alert(error.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5">
      <StudioHero sampleVideos={sampleVideos} heroSampleId={heroSampleId} setHeroSampleId={setHeroSampleId} onRequest={openRequestForm} requests={requests} />
      <ToolAndAiStrip />
      <div className="flex flex-wrap gap-2">
        {[ ["samples", "İlham Videoları"], ["new", "Teknik Talep Oluştur"], ["requests", "Taleplerim"], ["messages", "Editör & Destek Mesajları"] ].map(([key, label]) => <button key={key} type="button" onClick={() => setActiveTab(key)} className={cn("rounded-2xl border px-5 py-3 text-sm font-black transition", activeTab === key ? "border-yellow-500 bg-yellow-500 text-black" : "border-white/10 bg-black/35 text-white/60 hover:border-yellow-500/25 hover:text-yellow-300")}>{label}</button>)}
        <button type="button" onClick={refreshAll} className="ml-auto rounded-2xl border border-white/10 bg-black/35 px-5 py-3 text-sm font-bold text-white/60 hover:border-yellow-500/25 hover:text-yellow-300">Yenile</button>
      </div>

      {activeTab === "samples" ? <SampleGrid samples={sampleVideos} onRequest={openRequestForm} /> : null}
      {activeTab === "new" ? <RequestForm submit={submit} serviceId={serviceId} setServiceId={setServiceId} sampleId={sampleId} setSampleId={setSampleId} sampleVideos={sampleVideos} priority={priority} setPriority={setPriority} contentGoal={contentGoal} setContentGoal={setContentGoal} formatType={formatType} setFormatType={setFormatType} mood={mood} setMood={setMood} technicalNeeds={technicalNeeds} toggleNeed={toggleNeed} description={description} setDescription={setDescription} file={file} setFile={setFile} saving={saving} /> : null}
      {activeTab === "requests" ? <PublisherRequests requests={requests} /> : null}
        {activeTab === "messages" ? (
          <UnifiedMessagesModule
            currentUser={{ id: 999, username: "editor", fullName: "Editör Masası", role: "editor" }}
            userRole="editor"
            theme="dark"
          />
        ) : null}
    </div>
  );
}

function SampleGrid({ samples, onRequest, actionLabel = "Benzerini İste" }) {
  const [activeCategory, setActiveCategory] = useState("Tümü");
  const activeSamples = samples.filter((item) => (item.status || "active") === "active");
  const categories = useMemo(
    () => ["Tümü", ...Array.from(new Set(activeSamples.map((item) => item.category).filter(Boolean)))],
    [activeSamples]
  );
  const visibleSamples = activeCategory === "Tümü" ? activeSamples : activeSamples.filter((item) => item.category === activeCategory);

  if (!activeSamples.length) {
    return (
      <div className="rounded-[1.6rem] border border-white/10 bg-black/35 p-6 text-sm text-white/45">
        Henüz ilham videosu eklenmedi. Edit Admin panelinden 9:16 telefon formatında örnek video yükleyebilirsin.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {categories.length > 2 ? (
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <button
              key={category}
              type="button"
              onClick={() => setActiveCategory(category)}
              className={cn(
                "rounded-full border px-4 py-2 text-xs font-black uppercase tracking-[0.1em] transition",
                activeCategory === category
                  ? "border-yellow-500 bg-yellow-500 text-black"
                  : "border-white/10 bg-black/35 text-white/55 hover:border-yellow-500/30 hover:text-yellow-200"
              )}
            >
              {category}
            </button>
          ))}
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {visibleSamples.map((video) => {
          const tags = normalizeTags(video.tags);
          const previewUrl = getSamplePreviewUrl(video);
          const detailUrl = getSampleDetailUrl(video);

          return (
            <div key={video.id || video.title} className="group overflow-hidden rounded-[1.7rem] border border-white/10 bg-black/35 transition hover:-translate-y-1 hover:border-yellow-500/30 hover:shadow-[0_0_44px_rgba(234,179,8,.12)]">
              <div className="relative bg-[radial-gradient(circle_at_top,rgba(234,179,8,.16),transparent_60%)] p-4">
                <div className="relative mx-auto max-w-[210px] overflow-hidden rounded-[1.7rem] border border-yellow-500/25 bg-black p-1.5 shadow-[0_0_35px_rgba(234,179,8,.12)]">
                  <div className="pointer-events-none absolute left-1/2 top-1.5 z-20 h-3 w-16 -translate-x-1/2 rounded-b-xl bg-black" />
                  <video src={previewUrl} controls muted playsInline className="aspect-[9/16] w-full rounded-[1.25rem] object-cover transition duration-500 group-hover:scale-105" />
                  <div className="absolute right-3 top-3 rounded-full bg-black/70 px-3 py-1 text-[10px] font-bold text-white/70">
                    {getSampleDurationText(video)}
                  </div>
                </div>
              </div>

              <div className="p-5">
                <div className="text-lg font-black text-white">{video.title}</div>
                <div className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-yellow-300/75">{video.category}</div>
                <p className="mt-3 text-sm leading-6 text-white/55">{video.description}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {tags.map((tag) => <span key={tag} className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[10px] font-bold text-white/50">{tag}</span>)}
                  {detailUrl && detailUrl !== previewUrl ? <span className="rounded-full border border-yellow-500/25 bg-yellow-500/10 px-3 py-1 text-[10px] font-bold text-yellow-200">Detay video var</span> : null}
                </div>
                <button type="button" onClick={() => onRequest(video)} className="mt-4 w-full rounded-xl bg-yellow-500 px-3.5 py-1.5 text-[11px] font-black text-black hover:bg-yellow-300">{actionLabel}</button>
              </div>
            </div>
          );
        })}
      </div>

      {!visibleSamples.length ? (
        <div className="rounded-[1.6rem] border border-white/10 bg-black/35 p-6 text-sm text-white/45">
          Bu kategoride örnek video bulunmuyor.
        </div>
      ) : null}
    </div>
  );
}

function RequestForm(props) {
  return (
    <form onSubmit={props.submit} className="rounded-[1.8rem] border border-yellow-500/20 bg-[linear-gradient(180deg,rgba(234,179,8,.055),rgba(0,0,0,.38))] p-5">
      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between"><div><div className="text-xl font-black text-white">Teknik Edit Talebi</div><div className="mt-1 text-sm text-white/45">Yayıncı ücret görmez. Talep incelendikten sonra edit admin ücret, not, durum ve teslim dosyasını girer.</div></div><div className="rounded-2xl border border-yellow-500/25 bg-black/35 px-4 py-3 text-xs font-semibold leading-5 text-yellow-100">Her talep için 1 revize hakkı dahildir.</div></div>
      <div className="grid gap-4 xl:grid-cols-[1fr_1fr]">
        <Select label="Teknik İhtiyaç" value={props.serviceId} onChange={props.setServiceId} options={TECHNICAL_NEED_OPTIONS.map((x) => ({ value: x.id, label: x.serviceName }))} />
        <Select label="İçerik Hedefi" value={props.contentGoal} onChange={props.setContentGoal} options={CONTENT_GOAL_OPTIONS.map((x) => ({ value: x, label: x }))} />
        <Select label="Format / Platform" value={props.formatType} onChange={props.setFormatType} options={FORMAT_OPTIONS.map((x) => ({ value: x, label: x }))} />
        <Select label="Görsel Stil" value={props.mood} onChange={props.setMood} options={MOOD_OPTIONS.map((x) => ({ value: x, label: x }))} />
        <Select label="Öncelik" value={props.priority} onChange={props.setPriority} options={[{ value: "normal", label: "Standart" }, { value: "fast", label: "Öncelikli" }, { value: "urgent", label: "Acil / Yayına yetişsin" }]} />
        <Select label="Referans Örnek Video" value={props.sampleId} onChange={props.setSampleId} options={[{ value: "", label: "Örnek video seçilmedi" }, ...props.sampleVideos.map((x) => ({ value: x.id, label: x.title }))]} />
      </div>
      <div className="mt-6"><div className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-white/45">Ek Teknik Seçenekler</div><div className="grid gap-3 xl:grid-cols-2">{TECHNICAL_GROUPS.map((group) => <div key={group.title} className="rounded-2xl border border-white/10 bg-black/28 p-4"><div className="mb-3 text-xs font-black uppercase tracking-[0.18em] text-yellow-300/80">{group.title}</div><div className="flex flex-wrap gap-2">{group.items.map((item) => { const active = props.technicalNeeds.includes(item); const locked = item === "1 revize hakkı dahil olsun"; return <button key={item} type="button" onClick={() => props.toggleNeed(item)} className={cn("rounded-full border px-3 py-2 text-[11px] font-bold transition", active ? "border-yellow-400/55 bg-yellow-500/15 text-yellow-200" : "border-white/10 bg-black/35 text-white/45 hover:border-yellow-500/25 hover:text-yellow-200", locked ? "cursor-default" : "")}>{item}</button>; })}</div></div>)}</div></div>
      <label className="mt-5 block"><span className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-white/45">Video / Görsel / Doküman Yükle</span><div className="flex cursor-pointer items-center justify-between gap-3 rounded-2xl border border-dashed border-yellow-500/30 bg-black/45 px-4 py-4 text-sm text-white/65 hover:border-yellow-400/55 hover:bg-yellow-500/10"><span>{props.file?.name || "Yayın kesiti, ekran görüntüsü, örnek video veya doküman seç"}</span><span className="rounded-xl bg-yellow-500 px-3.5 py-1.5 text-[11px] font-black text-black">Dosya Seç</span><input type="file" accept=".mp4,.mov,.jpg,.jpeg,.png,.webp,.pdf" className="hidden" onChange={(event) => props.setFile(event.target.files?.[0] || null)} /></div></label>
      <label className="mt-5 block"><span className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-white/45">Talep Detayı</span><textarea value={props.description} onChange={(event) => props.setDescription(event.target.value)} rows={5} placeholder="Örnek: Yayındaki şu anı kes, altyazı ekle, ilk 3 saniyeyi dikkat çekici yap, TikTok için 20 saniyelik format hazırla..." className="w-full resize-none rounded-2xl border border-white/10 bg-black/50 px-4 py-3 text-sm text-white outline-none placeholder:text-white/30 focus:border-yellow-500/40" /></label>
      <div className="mt-5 flex justify-end"><button disabled={props.saving} className="rounded-2xl bg-yellow-500 px-7 py-3 text-sm font-black text-black transition hover:bg-yellow-300 disabled:opacity-60">{props.saving ? "Gönderiliyor..." : "Talebi Edit Admin'e Gönder"}</button></div>
    </form>
  );
}

function Select({ label, value, onChange, options }) {
  return <label className="block"><span className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-white/45">{label}</span><select value={value} onChange={(event) => onChange(event.target.value)} className="h-12 w-full rounded-2xl border border-white/10 bg-black/50 px-4 text-sm text-white outline-none [color-scheme:dark] focus:border-yellow-500/40">{options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>;
}

function PublisherRequests({ requests }) {
  return <div className="overflow-hidden rounded-[1.6rem] border border-white/10 bg-black/35"><div className="grid grid-cols-[1fr_1fr_1fr_.9fr_.9fr] gap-3 border-b border-white/10 bg-white/[0.04] px-5 py-3 text-xs font-bold uppercase tracking-[0.16em] text-white/45"><div>Talep</div><div>Hizmet</div><div>Durum</div><div>Ücret</div><div>Revize / Güncelleme</div></div>{requests.length === 0 ? <div className="p-6 text-sm text-white/45">Henüz editör talebi bulunmuyor.</div> : requests.map((request) => { const priceText = safeNumber(request.finalPrice) > 0 ? formatMoney(request.finalPrice) : "Edit admin değerlendirecek"; return <div key={request.id || request.requestNo} className="grid grid-cols-[1fr_1fr_1fr_.9fr_.9fr] gap-3 border-b border-white/10 px-5 py-4 text-sm text-white/70 last:border-b-0"><div><div className="font-black text-white">{request.requestNo || `EDT-${request.id}`}</div><div className="mt-1 max-h-10 overflow-hidden text-xs leading-5 text-white/35">{request.description}</div></div><div>{getServiceName(request.serviceId, request.serviceName)}</div><div><StatusBadge status={request.status} />{request.adminNote && <div className="mt-1 text-xs text-yellow-200/65">{request.adminNote}</div>}</div><div className="font-bold text-yellow-200">{priceText}</div><div><div className="text-xs font-bold text-white/55">1 revize hakkı</div><div className="mt-1 text-white/40">{request.updatedAt || request.createdAt || "-"}</div>{request.status === "delivered" && <div className="mt-2 rounded-xl border border-white/10 bg-white/[0.035] px-3 py-2 text-[11px] text-white/50">Revize mesajını Yönetici Mesajları alanından iletebilirsin.</div>}</div></div>; })}</div>;
}

function EditAdminPanel({ requests, setRequests, sampleVideos, setSampleVideos, refreshAll }) {
  const [activeMenu, setActiveMenu] = useState("requests");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedRequestId, setSelectedRequestId] = useState(requests[0]?.id || null);
  const [adminDraft, setAdminDraft] = useState({
    status: "reviewing",
    finalPrice: "",
    adminNote: "",
    deliveryLink: "",
  });
  const [saving, setSaving] = useState(false);
  const [deliveryUploading, setDeliveryUploading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const selectedRequest =
    requests.find((item) => Number(item.id) === Number(selectedRequestId)) ||
    requests[0] ||
    null;

  useEffect(() => {
    if (!selectedRequestId && requests[0]?.id) {
      setSelectedRequestId(requests[0].id);
    }
  }, [requests, selectedRequestId]);

  useEffect(() => {
    if (!selectedRequest) return;
    setAdminDraft({
      status: selectedRequest.status || "reviewing",
      finalPrice: selectedRequest.finalPrice ?? selectedRequest.final_price ?? "",
      adminNote: selectedRequest.adminNote ?? selectedRequest.admin_note ?? selectedRequest.editor_note ?? "",
      deliveryLink: selectedRequest.deliveryFileUrl || selectedRequest.delivery_link || "",
    });
  }, [selectedRequest?.id, selectedRequest?.status, selectedRequest?.finalPrice, selectedRequest?.adminNote, selectedRequest?.delivery_link]);

  const summary = useMemo(
    () => ({
      total: requests.length,
      waiting: requests.filter((item) =>
        ["received", "reviewing", "waiting_price_approval", "İnceleniyor", "Bekliyor"].includes(item.status)
      ).length,
      progress: requests.filter((item) =>
        ["in_progress", "waiting_revision", "Kurguda", "İşlemde"].includes(item.status)
      ).length,
      delivered: requests.filter((item) =>
        ["delivered", "Tamamlandı", "Teslim Edildi"].includes(item.status)
      ).length,
      cancelled: requests.filter((item) =>
        ["cancelled", "İptal"].includes(item.status)
      ).length,
    }),
    [requests]
  );

  const filteredRequests = useMemo(() => {
    const query = search.trim().toLowerCase();

    return requests.filter((item) => {
      const matchesStatus =
        statusFilter === "all" ||
        item.status === statusFilter ||
        (statusFilter === "reviewing" && ["received", "reviewing", "waiting_price_approval", "İnceleniyor", "Bekliyor"].includes(item.status)) ||
        (statusFilter === "in_progress" && ["in_progress", "waiting_revision", "Kurguda", "İşlemde"].includes(item.status)) ||
        (statusFilter === "delivered" && ["delivered", "Tamamlandı", "Teslim Edildi"].includes(item.status)) ||
        (statusFilter === "cancelled" && ["cancelled", "İptal"].includes(item.status));

      if (!matchesStatus) return false;
      if (!query) return true;

      const searchText = [
        item.id,
        item.requestNo,
        item.request_no,
        item.publisherName,
        item.publisher_name,
        item.username,
        item.phone,
        item.hiddenId,
        item.hidden_id,
        item.serviceName,
        item.service_name,
        item.requestTitle,
        item.request_title,
        item.platform,
        item.contentCategory,
        item.content_category,
        item.description,
        item.notes,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchText.includes(query);
    });
  }, [requests, search, statusFilter]);

  const suggestedPrice = useMemo(() => {
    if (!selectedRequest) return { min: 0, max: 0 };

    const serviceName = String(
      firstRequestValue(selectedRequest, ["serviceName", "service_name"], "")
    ).toLowerCase();
    const base =
      serviceName.includes("uzun") ? 1800 :
      serviceName.includes("paket") ? 1400 :
      serviceName.includes("logo") || serviceName.includes("kapak") ? 550 :
      850;

    const urgency = selectedRequest.urgency === "urgent"
      ? 650
      : selectedRequest.urgency === "fast"
        ? 300
        : 0;

    const durationText = String(
      firstRequestValue(selectedRequest, ["videoDuration", "video_duration"], "")
    );
    const longVideoExtra = /\b([3-9]\d|[1-9]\d{2,})\b/.test(durationText) ? 450 : 0;

    return {
      min: base + urgency + longVideoExtra,
      max: base + urgency + longVideoExtra + 750,
    };
  }, [selectedRequest]);

  const selectedSample = useMemo(() => {
    if (!selectedRequest) return null;
    const sampleId = firstRequestValue(selectedRequest, ["sampleVideoId", "sample_video_id", "sample_ref"], "");
    return sampleVideos.find((item) => String(item.id) === String(sampleId) || item.title === sampleId) || null;
  }, [sampleVideos, selectedRequest]);

  const publisherFiles = useMemo(
    () => normalizeRequestAttachments(selectedRequest || {}),
    [selectedRequest]
  );

  const detailRows = useMemo(
    () => requestDetailRows(selectedRequest || {}),
    [selectedRequest]
  );

  async function handleRefresh() {
    setIsRefreshing(true);
    await refreshAll();
    setTimeout(() => setIsRefreshing(false), 400);
  }

  async function deleteRequest(reqId) {
    if (!reqId) return;
    if (!window.confirm("Bu talebi kalıcı olarak silmek istediğinize emin misiniz?")) return;

    try {
      setSaving(true);
      await apiJson("/editadmin-request-delete.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: reqId }),
      });
      setRequests((prev) => prev.filter((item) => Number(item.id) !== Number(reqId)));
      if (Number(selectedRequestId) === Number(reqId)) {
        const remaining = requests.filter((item) => Number(item.id) !== Number(reqId));
        setSelectedRequestId(remaining[0]?.id || null);
      }
      alert("Talep başarıyla silindi.");
    } catch (error) {
      alert(error.message || "Talep silinemedi.");
    } finally {
      setSaving(false);
    }
  }

  async function saveRequestChanges(overrides = {}) {
    if (!selectedRequest) return;

    const payload = {
      id: selectedRequest.id,
      status: overrides.status ?? adminDraft.status,
      finalPrice:
        overrides.finalPrice !== undefined
          ? overrides.finalPrice
          : adminDraft.finalPrice === ""
            ? ""
            : adminDraft.finalPrice,
      adminNote: overrides.adminNote ?? adminDraft.adminNote,
      deliveryLink: overrides.deliveryLink ?? adminDraft.deliveryLink,
    };

    try {
      setSaving(true);
      await apiJson("/editadmin-request-update.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      setRequests((prev) =>
        prev.map((item) =>
          Number(item.id) === Number(selectedRequest.id)
            ? {
                ...item,
                ...payload,
                delivery_link: payload.deliveryLink,
                deliveryFileUrl: payload.deliveryLink,
                editor_note: payload.adminNote,
                admin_note: payload.adminNote,
                final_price: payload.finalPrice,
                price: payload.finalPrice || item.price,
                updatedAt: new Date().toLocaleString("tr-TR"),
              }
            : item
        )
      );
      alert("Değişiklikler başarıyla kaydedildi.");
    } catch (error) {
      alert(error.message || "Talep güncellenemedi.");
    } finally {
      setSaving(false);
    }
  }

  async function uploadDelivery(event) {
    const selectedFile = event.target.files?.[0] || null;
    event.target.value = "";
    if (!selectedFile || !selectedRequest) return;

    const formData = new FormData();
    formData.append("requestId", selectedRequest.id);
    formData.append("file", selectedFile);

    try {
      setDeliveryUploading(true);
      const res = await apiJson("/editadmin-delivery-upload.php", {
        method: "POST",
        body: formData,
      });
      setAdminDraft((prev) => ({
        ...prev,
        status: "delivered",
        deliveryLink: res.url || prev.deliveryLink,
      }));
      await refreshAll();
      alert("Teslim dosyası başarıyla yüklendi!");
    } catch (error) {
      alert(error.message || "Teslim dosyası yüklenemedi.");
    } finally {
      setDeliveryUploading(false);
    }
  }

  async function closeAsDelivered() {
    setAdminDraft((prev) => ({ ...prev, status: "delivered" }));
    await saveRequestChanges({ status: "delivered" });
  }

  return (
    <div className="space-y-6">
      {/* 👑 1. EXECUTIVE HEADER & SUB-MODULE TABS */}
      <div className="rounded-[2rem] border border-[#c9a24a]/30 bg-gradient-to-r from-[#17130a] via-[#100f0c] to-[#0d0d0f] p-5 md:p-6 shadow-2xl">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-amber-500/40 bg-amber-500/15 text-2xl shadow-[0_0_25px_rgba(234,179,8,0.2)]">
              👑
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#c9a24a]">Zakan Bilişim & Wolf Media</span>
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-0.5 text-[10px] font-bold text-emerald-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> Canlı Takip
                </span>
              </div>
              <h1 className="mt-1 text-2xl font-black text-white md:text-3xl">Editör Masası & Prodüksiyon Yönetimi</h1>
              <p className="mt-1 text-xs text-zinc-400">Yayıncılardan gelen video kurgu, efekt, afiş ve teknoloji destek taleplerini anlık yönetin.</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setActiveMenu("requests")}
              className={cn(
                "rounded-2xl px-5 py-3 text-xs font-black uppercase tracking-wider transition-all duration-200",
                activeMenu === "requests"
                  ? "bg-gradient-to-r from-[#e7b85d] to-[#d49e3d] text-black shadow-[0_0_25px_rgba(212,158,61,0.35)] scale-[1.02]"
                  : "border border-white/10 bg-black/40 text-zinc-400 hover:border-amber-500/40 hover:text-white"
              )}
            >
              📋 Gelen Talepler ({requests.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveMenu("samples")}
              className={cn(
                "rounded-2xl px-5 py-3 text-xs font-black uppercase tracking-wider transition-all duration-200",
                activeMenu === "samples"
                  ? "bg-gradient-to-r from-[#e7b85d] to-[#d49e3d] text-black shadow-[0_0_25px_rgba(212,158,61,0.35)] scale-[1.02]"
                  : "border border-white/10 bg-black/40 text-zinc-400 hover:border-amber-500/40 hover:text-white"
              )}
            >
              🎬 Showreel & İlham Videoları ({sampleVideos.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveMenu("tools")}
              className={cn(
                "rounded-2xl px-5 py-3 text-xs font-black uppercase tracking-wider transition-all duration-200",
                activeMenu === "tools"
                  ? "bg-gradient-to-r from-[#e7b85d] to-[#d49e3d] text-black shadow-[0_0_25px_rgba(212,158,61,0.35)] scale-[1.02]"
                  : "border border-white/10 bg-black/40 text-zinc-400 hover:border-amber-500/40 hover:text-white"
              )}
            >
              🛠️ Stüdyo Araçları
            </button>
            <button
              type="button"
              onClick={handleRefresh}
              className="flex items-center gap-1.5 rounded-2xl border border-[#c9a24a]/35 bg-[#c9a24a]/10 px-4 py-3 text-xs font-bold text-[#f0cd74] hover:bg-[#c9a24a] hover:text-black transition"
            >
              <span className={isRefreshing ? "animate-spin" : ""}>🔄</span> Yenile
            </button>
          </div>
        </div>

        {/* 📊 2. KPI METRICS (FILTER BUTONLARI OLARAK ÇALIŞIR) */}
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-5">
          <button
            type="button"
            onClick={() => setStatusFilter("all")}
            className={cn(
              "rounded-2xl border p-4 text-left transition-all",
              statusFilter === "all"
                ? "border-amber-400 bg-amber-500/20 shadow-[0_0_20px_rgba(234,179,8,0.25)]"
                : "border-white/10 bg-black/40 hover:border-white/20"
            )}
          >
            <div className="text-2xl font-black text-white">{summary.total}</div>
            <div className="mt-1 text-[10px] font-black uppercase tracking-wider text-amber-300">Tüm Talepler</div>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter("reviewing")}
            className={cn(
              "rounded-2xl border p-4 text-left transition-all",
              statusFilter === "reviewing"
                ? "border-orange-400 bg-orange-500/20 shadow-[0_0_20px_rgba(249,115,22,0.25)]"
                : "border-white/10 bg-black/40 hover:border-white/20"
            )}
          >
            <div className="text-2xl font-black text-orange-200">{summary.waiting}</div>
            <div className="mt-1 text-[10px] font-black uppercase tracking-wider text-orange-300">İncelemede / Teklif</div>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter("in_progress")}
            className={cn(
              "rounded-2xl border p-4 text-left transition-all",
              statusFilter === "in_progress"
                ? "border-fuchsia-400 bg-fuchsia-500/20 shadow-[0_0_20px_rgba(217,70,239,0.25)]"
                : "border-white/10 bg-black/40 hover:border-white/20"
            )}
          >
            <div className="text-2xl font-black text-fuchsia-200">{summary.progress}</div>
            <div className="mt-1 text-[10px] font-black uppercase tracking-wider text-fuchsia-300">Kurguda / İşlemde</div>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter("delivered")}
            className={cn(
              "rounded-2xl border p-4 text-left transition-all",
              statusFilter === "delivered"
                ? "border-emerald-400 bg-emerald-500/20 shadow-[0_0_20px_rgba(16,185,129,0.25)]"
                : "border-white/10 bg-black/40 hover:border-white/20"
            )}
          >
            <div className="text-2xl font-black text-emerald-200">{summary.delivered}</div>
            <div className="mt-1 text-[10px] font-black uppercase tracking-wider text-emerald-300">Teslim Edildi</div>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter("cancelled")}
            className={cn(
              "rounded-2xl border p-4 text-left transition-all col-span-2 sm:col-span-1",
              statusFilter === "cancelled"
                ? "border-red-400 bg-red-500/20 shadow-[0_0_20px_rgba(239,68,68,0.25)]"
                : "border-white/10 bg-black/40 hover:border-white/20"
            )}
          >
            <div className="text-2xl font-black text-red-300">{summary.cancelled}</div>
            <div className="mt-1 text-[10px] font-black uppercase tracking-wider text-red-400">İptal / Red</div>
          </button>
        </div>
      </div>

      {activeMenu === "requests" ? (
        <>
          {/* 🔍 3. TALEP LİSTESİ VE TABLO ALANI */}
          <PanelCard className="p-6">
            <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-xl font-black tracking-wide text-white">Gelen Yayıncı Editör Talepleri</h2>
                <p className="mt-1 text-xs text-zinc-400">Yayıncıların web panelinden ilettiği montaj, kesit, afiş ve ses/teknoloji talepleri.</p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <div className="relative min-w-[280px]">
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="🔍 Yayıncı, @kullanıcı, telefon veya hizmet ara..."
                    className="w-full rounded-xl border border-[#c9a24a]/25 bg-black/50 px-4 py-2.5 text-xs text-white placeholder:text-zinc-500 outline-none focus:border-amber-400"
                  />
                  {search && (
                    <button type="button" onClick={() => setSearch("")} className="absolute right-3 top-2.5 text-xs text-zinc-400 hover:text-white">✕</button>
                  )}
                </div>

                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  className="rounded-xl border border-[#c9a24a]/25 bg-black/50 px-4 py-2.5 text-xs text-white outline-none focus:border-amber-400"
                >
                  <option value="all">Filtre: Tüm Durumlar ({requests.length})</option>
                  <option value="reviewing">İncelemede / Teklif ({summary.waiting})</option>
                  <option value="in_progress">Kurguda / İşlemde ({summary.progress})</option>
                  <option value="delivered">Teslim Edilenler ({summary.delivered})</option>
                  <option value="cancelled">İptal Edilenler ({summary.cancelled})</option>
                </select>
              </div>
            </div>

            {/* TABLO */}
            <AdminRequestTable
              requests={filteredRequests}
              onSelect={setSelectedRequestId}
              selectedId={selectedRequest?.id}
            />
          </PanelCard>

          {/* 🎬 4. DETAY & KURGU YÖNETİMİ / İNCELEME PANELİ */}
          {selectedRequest ? (
            <PanelCard className="p-6 border-amber-500/30">
              <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between border-b border-white/10 pb-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <span className="rounded-xl bg-amber-500/20 border border-amber-500/40 px-3 py-1 font-mono text-xs font-black text-amber-300">
                      {selectedRequest.requestNo || `EDT-${selectedRequest.id}`}
                    </span>
                    <h2 className="text-xl font-black text-white">
                      {selectedRequest.serviceName || selectedRequest.service_name || "Özel Edit Talebi"}
                    </h2>
                    <StatusBadge status={selectedRequest.status} />
                    <span className={cn("rounded-full border px-3 py-1 text-xs font-bold", urgencyClass(selectedRequest.urgency))}>
                      {urgencyLabel(selectedRequest.urgency)}
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-zinc-400">
                    Oluşturulma: {selectedRequest.createdAt || selectedRequest.created_at || "-"} • Son Güncelleme: {selectedRequest.updatedAt || selectedRequest.updated_at || "-"}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <a
                    href={
                      selectedRequest.phone
                        ? `https://wa.me/${selectedRequest.phone.replace(/\D/g, "").startsWith("90") ? selectedRequest.phone.replace(/\D/g, "") : "90" + selectedRequest.phone.replace(/\D/g, "").replace(/^0/, "")}?text=${encodeURIComponent("Merhaba " + (selectedRequest.publisherName || selectedRequest.username) + ", Wolf Media x Zakan Bilişim Editör masası olarak talebiniz hakkında iletişime geçiyorum.")}`
                        : "https://wa.me/905470353455"
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-xl bg-[#25D366] px-4 py-2.5 text-xs font-black text-black shadow-md hover:brightness-110 transition"
                  >
                    💬 WhatsApp ile İletişime Geç
                  </a>
                  <button
                    type="button"
                    onClick={() => deleteRequest(selectedRequest.id)}
                    className="rounded-xl border border-red-500/30 bg-red-500/10 px-3.5 py-2.5 text-xs font-bold text-red-300 hover:bg-red-500/20 transition"
                  >
                    🗑️ Sil
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.1fr_0.9fr]">
                {/* SOL KOLON: YAYINCI BİLGİLERİ VE TALEBİN DETAYLARI */}
                <div className="space-y-5">
                  {/* Yayıncı Kartı */}
                  <div className="rounded-2xl border border-white/10 bg-black/40 p-4">
                    <div className="text-xs font-black uppercase tracking-wider text-amber-300 mb-3">Yayıncı Bilgileri</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <InfoBox label="Yayıncı Adı" value={selectedRequest.publisherName || selectedRequest.publisher_name} />
                      <InfoBox label="TikTok Kullanıcı Adı" value={selectedRequest.username ? `@${selectedRequest.username}` : "-"} />
                      <InfoBox label="Telefon / WhatsApp" value={selectedRequest.phone || "Girilmedi"} />
                      <InfoBox label="Gizli ID" value={selectedRequest.hiddenId || selectedRequest.hidden_id || "-"} />
                      <InfoBox label="Hizmet Kapsamı" value={selectedRequest.serviceName || selectedRequest.service_name} />
                      <InfoBox label="Revize Hakkı" value="1 Hak Dahil" />
                    </div>
                  </div>

                  {/* Yayıncı Talep Açıklaması */}
                  <div className="rounded-2xl border border-zinc-800 bg-black/40 p-4">
                    <div className="mb-2 text-xs font-black uppercase tracking-wider text-amber-300">Yayıncı Talebi & Notu</div>
                    <pre className="whitespace-pre-wrap font-sans text-xs leading-6 text-zinc-200 bg-black/30 p-3.5 rounded-xl border border-white/5">
                      {selectedRequest.description || selectedRequest.notes || "Açıklama bulunmuyor."}
                    </pre>
                  </div>

                  {/* Seçilen Referans Showreel / Video */}
                  {selectedSample && (
                    <div className="rounded-2xl border border-yellow-500/20 bg-yellow-500/[0.05] p-4">
                      <div className="mb-3 text-xs font-black uppercase tracking-wider text-amber-300">Seçilen İlham / Örnek Video</div>
                      <div className="grid gap-3 sm:grid-cols-[160px_1fr]">
                        <div className="relative aspect-[9/16] w-full max-w-[160px] overflow-hidden rounded-xl bg-black">
                          <video src={getSamplePreviewUrl(selectedSample)} controls muted playsInline className="h-full w-full object-cover" />
                        </div>
                        <div>
                          <div className="text-sm font-bold text-white">{selectedSample.title}</div>
                          <div className="mt-1 text-xs font-bold text-amber-400">{selectedSample.category || "Showreel"}</div>
                          <p className="mt-2 text-xs leading-5 text-white/60">{selectedSample.description}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Yüklenen Dosyalar & Kaynak Linkleri */}
                  <div className="rounded-2xl border border-white/10 bg-black/40 p-4">
                    <div className="mb-3 text-xs font-black uppercase tracking-wider text-amber-300">Yayıncının Yüklediği Ham Dosyalar & Linkler</div>
                    {publisherFiles.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {publisherFiles.map((file, index) => (
                          <FileCard key={file.id || index} title={file.category || `Kaynak ${index + 1}`} fileName={file.name} fileUrl={file.url} />
                        ))}
                      </div>
                    ) : (
                      <div className="text-xs text-zinc-500 p-3 rounded-xl bg-black/20 border border-white/5">
                        Yayıncı doğrudan dosya yüklememiş veya link belirtmiş.
                      </div>
                    )}
                  </div>
                </div>

                {/* SAĞ KOLON: EDİTÖRE ÖZEL YÖNETİM, ÜCRET VE TESLİMAT ALANI */}
                <div className="space-y-4 rounded-2xl border border-amber-500/30 bg-gradient-to-b from-[#14120c] to-[#0a0a0c] p-5 shadow-xl">
                  <div className="flex items-center justify-between border-b border-white/10 pb-3">
                    <div className="text-sm font-black uppercase tracking-wider text-[#d6b25e]">Prodüksiyon & Teslimat İstasyonu</div>
                    <span className="rounded-lg bg-amber-500/10 px-2.5 py-1 text-[10px] font-bold text-amber-300">Zakan Bilişim</span>
                  </div>

                  {/* Sistem Öneri Fiyatı */}
                  <div className="rounded-2xl border border-amber-400/25 bg-amber-400/10 p-4">
                    <div className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-300">Tahmini Kurgu Fiyat Önerisi</div>
                    <div className="mt-1 text-2xl font-black text-white">{formatMoney(suggestedPrice.min)} - {formatMoney(suggestedPrice.max)}</div>
                    <p className="mt-1 text-[11px] text-white/50">Hizmet türü, süre ve aciliyet durumuna göre sistem tahmini aralığı.</p>
                  </div>

                  {/* Talep Durumu */}
                  <FormField label="Talep Durumu">
                    <select
                      value={adminDraft.status}
                      onChange={(e) => setAdminDraft((prev) => ({ ...prev, status: e.target.value }))}
                      className="input-dark text-sm font-bold"
                    >
                      <option value="reviewing">🟡 İncelemede (Teklif Verilecek)</option>
                      <option value="in_progress">🟣 Kurguda / İşleme Alındı</option>
                      <option value="waiting_revision">🌸 Revize Aşamasında</option>
                      <option value="delivered">🟢 Teslim Edildi (Tamamlandı)</option>
                      <option value="cancelled">🔴 İptal Edildi</option>
                    </select>
                  </FormField>

                  {/* Ücret Gir */}
                  <FormField label="Talebe Özel Ücret (₺)">
                    <div className="space-y-2">
                      <div className="flex gap-1.5 flex-wrap">
                        {["₺500", "₺750", "₺1.000", "₺1.500", "₺2.000"].map((p) => (
                          <button
                            key={p}
                            type="button"
                            onClick={() => setAdminDraft((prev) => ({ ...prev, finalPrice: p.replace(/\D/g, "") }))}
                            className="rounded-lg border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-bold text-amber-200 hover:bg-amber-500/20"
                          >
                            {p}
                          </button>
                        ))}
                      </div>
                      <input
                        type="text"
                        value={adminDraft.finalPrice}
                        onChange={(e) => setAdminDraft((prev) => ({ ...prev, finalPrice: e.target.value }))}
                        className="input-dark"
                        placeholder="Örn: 750 veya Wolf İndirimli"
                      />
                    </div>
                  </FormField>

                  {/* Yayıncıya Görünecek Not */}
                  <FormField label="Yayıncıya İletilecek Editör Notu">
                    <textarea
                      value={adminDraft.adminNote}
                      onChange={(e) => setAdminDraft((prev) => ({ ...prev, adminNote: e.target.value }))}
                      className="input-dark min-h-20 resize-none text-xs"
                      placeholder="Örn: Kurgunuz tamamlandı, indirme linkini aşağıda bulabilirsiniz veya inceleme devam ediyor..."
                    />
                  </FormField>

                  {/* Teslimat Linki */}
                  <FormField label="Teslimat Linki (Drive / WeTransfer / YouTube vb.)">
                    <input
                      type="text"
                      value={adminDraft.deliveryLink}
                      onChange={(e) => setAdminDraft((prev) => ({ ...prev, deliveryLink: e.target.value }))}
                      className="input-dark text-xs"
                      placeholder="https://drive.google.com/... veya dosya indirme bağlantısı"
                    />
                  </FormField>

                  {/* Doğrudan Video Teslimatı Yükleme */}
                  <FormField label="Veya Doğrudan Teslim Videosu Yükle">
                    <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-dashed border-[#c9a24a]/40 bg-black/40 px-4 py-3 text-xs text-zinc-300 hover:border-amber-400 transition">
                      <span>{deliveryUploading ? "Yükleniyor..." : selectedRequest.deliveryFileName || "Video / ZIP Dosyası Seç"}</span>
                      <span className="rounded-lg bg-amber-500 px-3 py-1 text-[11px] font-bold text-black">Yükle</span>
                      <input type="file" accept=".mp4,.mov,.webm,.zip,.rar" className="hidden" disabled={deliveryUploading} onChange={uploadDelivery} />
                    </label>
                  </FormField>

                  {/* Aksiyon Butonları */}
                  <div className="pt-2 space-y-2.5">
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => saveRequestChanges()}
                      className="w-full rounded-xl bg-gradient-to-r from-[#e7b85d] to-[#d49e3d] px-4 py-3 text-xs font-black uppercase tracking-wider text-black shadow-lg hover:brightness-110 transition disabled:opacity-50"
                    >
                      {saving ? "Kaydediliyor..." : "💾 Değişiklikleri Kaydet"}
                    </button>

                    <button
                      type="button"
                      disabled={saving}
                      onClick={closeAsDelivered}
                      className="w-full rounded-xl border border-emerald-500/40 bg-emerald-500/15 px-4 py-3 text-xs font-black uppercase tracking-wider text-emerald-200 hover:bg-emerald-500/25 transition disabled:opacity-50"
                    >
                      🚀 Teslim Edildi Olarak Kapat
                    </button>
                  </div>
                </div>
              </div>
            </PanelCard>
          ) : null}
        </>
      ) : null}

      {activeMenu === "samples" ? (
        <SampleVideoManager
          sampleVideos={sampleVideos}
          setSampleVideos={setSampleVideos}
          refreshAll={refreshAll}
        />
      ) : null}

      {activeMenu === "tools" ? <ToolAndAiStrip /> : null}
    </div>
  );
}

function AdminMetricCard({ title, value, tone = "yellow" }) {
  const toneClass =
    tone === "green"
      ? "border-emerald-400/20 bg-emerald-500/10 text-emerald-200"
      : tone === "purple"
        ? "border-fuchsia-400/20 bg-fuchsia-500/10 text-fuchsia-200"
        : tone === "orange"
          ? "border-orange-400/20 bg-orange-500/10 text-orange-200"
          : "border-yellow-500/20 bg-yellow-500/10 text-yellow-200";

  return (
    <div className={cn("rounded-2xl border p-4", toneClass)}>
      <div className="text-2xl font-black text-white">{value}</div>
      <div className="mt-1 text-[10px] font-black uppercase tracking-[0.18em]">
        {title}
      </div>
    </div>
  );
}

function SideButton({ children, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold transition",
        active ? "bg-[#c9a24a] text-black" : "text-zinc-400 hover:bg-white/5 hover:text-white"
      )}
    >
      {children}
    </button>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-zinc-300">{label}</span>
      {children}
    </label>
  );
}

function InfoBox({ label, value }) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-black/30 p-4">
      <p className="text-xs uppercase tracking-wide text-zinc-500">{label}</p>
      <p className="mt-2 break-words font-semibold text-white">{value || "-"}</p>
    </div>
  );
}

function LinkInfoBox({ label, value }) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-black/30 p-4">
      <p className="text-xs uppercase tracking-wide text-zinc-500">{label}</p>
      <a
        href={value}
        target="_blank"
        rel="noreferrer"
        className="mt-2 block break-all font-semibold text-[#d6b25e] hover:text-yellow-200"
      >
        Bağlantıyı Aç
      </a>
    </div>
  );
}

function FileCard({ title, fileName, fileUrl }) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-black/30 p-4">
      <div className="mb-2 text-sm font-semibold text-[#d6b25e]">{title}</div>
      <p className="break-all text-sm text-zinc-300">{fileName || "-"}</p>
      {fileUrl ? (
        <a
          href={fileUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-block rounded-xl border border-[#c9a24a]/30 px-3 py-2 text-xs font-semibold text-[#d6b25e]"
        >
          Görüntüle / İndir
        </a>
      ) : (
        <span className="mt-3 inline-block rounded-xl border border-[#c9a24a]/20 px-3 py-2 text-xs font-semibold text-zinc-500">
          Dosya bağlantısı yok
        </span>
      )}
    </div>
  );
}

function AdminRequestTable({ requests, onSelect, selectedId }) {
  if (!requests.length) {
    return (
      <div className="rounded-2xl border border-zinc-800 bg-black/30 p-6 text-sm text-zinc-400">
        Kayıt bulunamadı.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1120px] text-left text-sm">
        <thead className="text-xs uppercase text-zinc-500">
          <tr className="border-b border-zinc-800">
            <th className="py-3">Talep No</th>
            <th>Yayıncı</th>
            <th>Başlık / Hizmet</th>
            <th>Platform</th>
            <th>Öncelik</th>
            <th>Ücret</th>
            <th>Durum</th>
            <th>Güncelleme</th>
            <th>İşlem</th>
          </tr>
        </thead>

        <tbody>
          {requests.map((request) => (
            <tr
              key={request.id}
              className={cn(
                "border-b border-zinc-900 text-zinc-300",
                Number(selectedId) === Number(request.id) && "bg-[#c9a24a]/5"
              )}
            >
              <td className="py-4 font-semibold text-white">
                {request.requestNo || request.request_no || `EDT-${request.id}`}
              </td>

              <td>
                {request.publisherName || request.publisher_name || "-"}
                <div className="text-xs text-zinc-500">@{request.username || "-"}</div>
              </td>

              <td>
                <div className="max-w-[240px] truncate font-semibold text-white">
                  {firstRequestValue(
                    request,
                    ["requestTitle", "request_title"],
                    getServiceName(
                      request.serviceId || request.service_id,
                      request.serviceName || request.service_name
                    )
                  )}
                </div>
                <div className="mt-1 text-xs text-zinc-500">
                  {getServiceName(
                    request.serviceId || request.service_id,
                    request.serviceName || request.service_name
                  )}
                </div>
              </td>

              <td>
                {firstRequestValue(request, ["platform", "targetPlatform", "target_platform"], "-")}
              </td>

              <td>
                <span
                  className={cn(
                    "rounded-full border px-2.5 py-1 text-[10px] font-bold",
                    urgencyClass(request.urgency)
                  )}
                >
                  {urgencyLabel(request.urgency)}
                </span>
              </td>

              <td>
                {safeNumber(request.finalPrice || request.final_price) > 0
                  ? formatMoney(request.finalPrice || request.final_price)
                  : "Değerlendirilecek"}
              </td>

              <td>
                <StatusBadge status={request.status} />
              </td>

              <td className="text-zinc-500">
                {request.updatedAt || request.updated_at || request.createdAt || "-"}
              </td>

              <td>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => onSelect?.(request.id)}
                    className="rounded-xl border border-[#c9a24a]/30 px-3 py-2 text-xs font-semibold text-[#d6b25e] transition hover:bg-[#c9a24a]/10"
                  >
                    Detay
                  </button>
                  <a
                    href={
                      request.phone
                        ? `https://wa.me/${request.phone.replace(/\D/g, "").startsWith("90") ? request.phone.replace(/\D/g, "") : "90" + request.phone.replace(/\D/g, "").replace(/^0/, "")}?text=${encodeURIComponent("Merhaba " + (request.publisherName || request.username) + ", Wolf Media Editör Masası olarak talebiniz hakkında iletişime geçiyorum.")}`
                        : "https://wa.me/905470353455"
                    }
                    target="_blank"
                    rel="noreferrer"
                    title="WhatsApp'tan Yaz"
                    className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-2 text-xs font-bold text-emerald-300 hover:bg-emerald-500/20"
                  >
                    💬
                  </a>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SampleVideoManager({ sampleVideos, setSampleVideos, refreshAll }) {
  const emptyForm = {
    id: "",
    title: "",
    category: "",
    platform: "TikTok",
    formatRatio: "9:16",
    duration: "",
    durationType: "standard",
    description: "",
    status: "active",
    videoUrl: "",
    detailVideoUrl: "",
    tags: "",
    sortOrder: 0,
  };

  const [form, setForm] = useState(emptyForm);
  const [previewFile, setPreviewFile] = useState(null);
  const [detailFile, setDetailFile] = useState(null);
  const [saving, setSaving] = useState(false);

  function edit(video) {
    setForm({
      id: video.id || "",
      title: video.title || "",
      category: video.category || "",
      platform: video.platform || "TikTok",
      formatRatio: video.formatRatio || video.format_ratio || "9:16",
      duration: "",
      durationType: "",
      description: video.description || "",
      status: video.status || "active",
      videoUrl: video.videoUrl || video.videoPath || "",
      detailVideoUrl: video.detailVideoUrl || video.longVideoUrl || "",
      tags: normalizeTags(video.tags).join(", "),
      sortOrder: video.sortOrder || video.sort_order || 0,
    });
    setPreviewFile(null);
    setDetailFile(null);
  }

  async function save(event) {
    event.preventDefault();

    if (!form.title.trim() || !form.category.trim()) {
      alert("Başlık ve kategori zorunludur.");
      return;
    }

    if (!form.id && !previewFile && !form.videoUrl.trim()) {
      alert("En az bir kısa önizleme videosu yüklemelisin.");
      return;
    }

    try {
      setSaving(true);
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => formData.append(key, value ?? ""));
      if (previewFile) formData.append("video", previewFile);
      if (detailFile) formData.append("detailVideo", detailFile);

      await apiJson("/editadmin-sample-video-save.php", { method: "POST", body: formData });
      setForm(emptyForm);
      setPreviewFile(null);
      setDetailFile(null);
      await refreshAll();
      alert("İlham videosu kalıcı olarak kaydedildi.");
    } catch (error) {
      alert(error.message || "Video kaydedilemedi. API dosyalarını kontrol et.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <PanelCard>
      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-wide text-white">İlham Video Yönetimi</h2>
          <p className="mt-1 text-sm leading-6 text-zinc-400">
            Yayıncı panelindeki showreel videoları burada kalıcı kaydedilir. Öneri: 1080x1920, 9:16 telefon formatı.
          </p>
        </div>
        <div className="rounded-2xl border border-yellow-500/25 bg-yellow-500/10 px-4 py-3 text-xs font-bold leading-5 text-yellow-100">
          Süre sınırı yoktur.<br />Önerilen format: MP4, 9:16 telefon/TikTok görünümü.
        </div>
      </div>

      <form onSubmit={save} className="mb-6 space-y-4 rounded-[1.5rem] border border-yellow-500/15 bg-black/25 p-4">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
          <input className="input-dark" placeholder="Başlık" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          <input className="input-dark" placeholder="Kategori" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} required />
          <input className="input-dark" placeholder="Platform / format örn: TikTok 9:16" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} />
          <input className="input-dark" placeholder="Format oranı örn: 9:16" value={form.formatRatio} onChange={(e) => setForm({ ...form, formatRatio: e.target.value })} />
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <label className="block rounded-2xl border border-dashed border-[#c9a24a]/30 bg-black/30 p-4">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-yellow-300">Kısa Önizleme Videosu</div>
            <div className="mt-1 text-xs leading-5 text-zinc-400">Yayıncı kartında/hero alanında görünür. 9:16 telefon formatı önerilir.</div>
            <input type="file" accept="video/mp4,video/quicktime,.mp4,.mov,.webm" className="mt-3 block w-full text-xs text-zinc-300 file:mr-4 file:rounded-lg file:border-0 file:bg-yellow-500 file:px-4 file:py-2 file:text-xs file:font-bold file:text-black" onChange={(e) => setPreviewFile(e.target.files?.[0] || null)} />
            <div className="mt-2 text-xs font-semibold text-white/55">{previewFile?.name || form.videoUrl || "Henüz video seçilmedi"}</div>
          </label>

          <label className="block rounded-2xl border border-dashed border-white/15 bg-black/30 p-4">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-yellow-300">Detay Örnek Videosu</div>
            <div className="mt-1 text-xs leading-5 text-zinc-400">İstenirse detaylı örnek için kullanılır. Süre sınırı yoktur, 9:16 format önerilir.</div>
            <input type="file" accept="video/mp4,video/quicktime,.mp4,.mov,.webm" className="mt-3 block w-full text-xs text-zinc-300 file:mr-4 file:rounded-lg file:border-0 file:bg-white/15 file:px-4 file:py-2 file:text-xs file:font-bold file:text-white" onChange={(e) => setDetailFile(e.target.files?.[0] || null)} />
            <div className="mt-2 text-xs font-semibold text-white/55">{detailFile?.name || form.detailVideoUrl || "Opsiyonel"}</div>
          </label>
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_1fr_.5fr_.5fr]">
          <input className="input-dark" placeholder="Kısa video URL / mevcut bağlantı" value={form.videoUrl} onChange={(e) => setForm({ ...form, videoUrl: e.target.value })} />
          <input className="input-dark" placeholder="Detay video URL / mevcut bağlantı" value={form.detailVideoUrl} onChange={(e) => setForm({ ...form, detailVideoUrl: e.target.value })} />
          <input className="input-dark" placeholder="Sıra" type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: e.target.value })} />
          <select className="input-dark" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option value="active">Aktif</option>
            <option value="passive">Pasif</option>
          </select>
        </div>

        <input className="input-dark" placeholder="Etiketler: Kesit, Altyazı, 9:16" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
        <textarea className="input-dark min-h-24 resize-none" placeholder="Açıklama" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />

        <div className="flex flex-wrap justify-end gap-2">
          {form.id ? <button type="button" onClick={() => { setForm(emptyForm); setPreviewFile(null); setDetailFile(null); }} className="rounded-xl border border-white/10 px-4 py-2 text-sm font-semibold text-white/65 hover:text-white">Vazgeç</button> : null}
          <button disabled={saving} className="rounded-xl bg-[#c9a24a] px-5 py-2.5 text-sm font-semibold text-black disabled:opacity-60">
            {saving ? "Kaydediliyor..." : form.id ? "Güncelle" : "Kalıcı Kaydet"}
          </button>
        </div>
      </form>

      
      <div className="space-y-4">
        <h3 className="text-sm font-bold uppercase tracking-wider text-yellow-300">Yüklü İlham Videoları ({sampleVideos.length})</h3>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {sampleVideos.map((video) => (
            <div key={video.id || video.title} className="group overflow-hidden rounded-[1.7rem] border border-white/10 bg-black/40 p-4 transition hover:border-yellow-500/30">
              <div className="relative aspect-[9/16] w-full overflow-hidden rounded-xl bg-black">
                <video src={getSamplePreviewUrl(video)} controls muted playsInline className="h-full w-full object-cover" />
              </div>
              <div className="mt-3">
                <div className="font-bold text-white text-sm">{video.title}</div>
                <div className="text-xs text-yellow-400/80">{video.category || "Viral Kesit"}</div>
                <div className="mt-3 flex gap-2">
                  <button type="button" onClick={() => edit(video)} className="flex-1 rounded-xl bg-[#c9a24a] py-2 text-xs font-bold text-black hover:bg-yellow-400">Düzenle</button>
                  <button type="button" onClick={async () => {
                    if (window.confirm(`"${video.title}" videosunu silmek istediğinize emin misiniz?`)) {
                      try {
                        await apiJson("/editadmin-sample-video-delete.php", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: video.id }) });
                        await refreshAll();
                      } catch (err) {
                        alert(err.message || "Video silinemedi.");
                      }
                    }
                  }} className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs font-bold text-red-300 hover:bg-red-500/20">🗑️</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

    </PanelCard>
  );
}

export default function EditorSupportModulePreview({ embedded = false, defaultView = "publisher", currentAdmin = {} } = {}) {
  useMobileOnlyResponsiveLayout("wm-editor-support-mobile", !embedded);

  const [roleView, setRoleView] = useState(() => {
    if (defaultView) return defaultView;
    return typeof window !== "undefined" && window.location.pathname.includes("edit-admin") ? "edit_admin" : "publisher";
  });
  const [requests, setRequests] = useState(FALLBACK_REQUESTS);
  const [sampleVideos, setSampleVideos] = useState(FALLBACK_SAMPLES);
  const [apiWarning, setApiWarning] = useState("");
  const showRoleToggle = !embedded;

  const previewPublisher = useMemo(() => {
    const source = requests.find((item) => item?.publisherId || item?.publisher_id || item?.username) || requests[0] || {};

    return {
      id: source.publisherId || source.publisher_id || "",
      fullName: source.publisherName || source.publisher_name || "Yayıncı Önizleme",
      username: source.username || "publisher-preview",
      hiddenId: source.hiddenId || source.hidden_id || "-",
      status: "Aktif",
    };
  }, [requests]);

  async function refreshAll() {
    let warning = "";

    try {
      const samples = await apiJson("/editor-sample-videos.php");
      const sampleList = Array.isArray(samples.data) ? samples.data : [];
      setSampleVideos(sampleList.length ? sampleList : FALLBACK_SAMPLES);
    } catch (error) {
      console.error("İlham video API hatası:", error);
      warning = "İlham video listesi okunamadı. /api/editor-sample-videos.php dosyasını kontrol edin.";
    }

    try {
      const requestList = await apiJson("/editadmin-requests-list.php");
      setRequests(Array.isArray(requestList.data) ? requestList.data : []);
    } catch (error) {
      console.error("Editör talep API hatası:", error);
      warning = warning || "Talep listesi okunamadı; fakat ilham videoları kalıcı olarak gösterilmeye devam eder.";
    }

    setApiWarning(warning);
  }

  useEffect(() => { refreshAll(); }, []);

  return (
    <div className={embedded ? "text-white" : "min-h-screen bg-[#070707] p-4 text-white md:p-8"}>
      <style>{`
        .input-dark { width: 100%; min-height: 44px; border-radius: 14px; border: 1px solid rgba(201, 162, 74, 0.2); background: rgba(0,0,0,0.4); color: white; padding: 0 12px; outline: none; }
        .input-dark:focus { border-color: rgba(201, 162, 74, 0.7); box-shadow: 0 0 0 3px rgba(201, 162, 74, 0.08); }
        textarea.input-dark { padding-top: 12px; padding-bottom: 12px; height: auto; }
        select.input-dark option { background: #111; color: white; }
      `}</style>
      <div className={embedded ? "space-y-6" : "mx-auto max-w-7xl space-y-6"}>
        <div className="flex flex-col justify-between gap-4 rounded-3xl border border-[#c9a24a]/20 bg-gradient-to-br from-[#15110a] to-[#0b0b0b] p-6 shadow-2xl shadow-black/40 md:flex-row md:items-center"><div><p className="text-xs uppercase tracking-[0.4em] text-[#c9a24a]">Wolf Media Ajans</p><h1 className="mt-3 text-2xl font-bold text-white md:text-3xl">Editör Desteği ve Talep Yönetimi</h1><p className="mt-2 max-w-2xl text-sm text-zinc-400">Yayıncı talepleri, örnek çalışma vitrini, ücret / not / teslim süreci ve revize takibi admin paneli içerisinden yönetilir.</p></div>{showRoleToggle ? <div className="flex rounded-2xl border border-[#c9a24a]/20 bg-black/30 p-1"><button type="button" onClick={() => setRoleView("publisher")} className={cn("rounded-xl px-4 py-2 text-sm font-semibold transition", roleView === "publisher" ? "bg-[#c9a24a] text-black" : "text-zinc-400")}>Publisher</button><button type="button" onClick={() => setRoleView("edit_admin")} className={cn("rounded-xl px-4 py-2 text-sm font-semibold transition", roleView === "edit_admin" ? "bg-[#c9a24a] text-black" : "text-zinc-400")}>Edit Admin</button></div> : <div className="rounded-2xl border border-[#c9a24a]/25 bg-[#c9a24a]/10 px-4 py-3 text-xs font-black uppercase tracking-[0.16em] text-[#f1d184]">Admin içi modül</div>}</div>
        {apiWarning ? <div className="rounded-2xl border border-yellow-400/20 bg-yellow-400/10 p-4 text-sm text-yellow-100">{apiWarning}</div> : null}
        {roleView === "publisher" ? (
          <PublisherEditorSupportPanel
            currentPublisher={previewPublisher}
            previewMode
            previewSamples={sampleVideos}
            previewRequests={requests}
            onPreviewRefresh={refreshAll}
          />
        ) : (
          <EditAdminPanel
            requests={requests}
            setRequests={setRequests}
            sampleVideos={sampleVideos}
            setSampleVideos={setSampleVideos}
            refreshAll={refreshAll}
          />
        )}
      </div>
    </div>
  );
}
